import React, { Component } from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { Dropdown, Select, Ref } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import { commonPropTypes, formFieldWrapper } from '../formFieldWrapper'
import { MODES, MODE_OPTIONS } from '../../../constants/optionLists'
import styles from '../styles.scss'
const cx = classnames.bind(styles)

/**
 * DropdownInputField
 *  The selected data value (input.value) is shaped differently depending on use as follows:
 *    - Single select (multiple = false): data corresponds to the selected item value (string, number or bool)
 *    - Multiple select: array of the values selected (array of string, number or bool)
 *    - Multiple with "modeSelect" option (which is another dropdown for selecting a separate
 *  value - ALL/INCLUDE/EXCLUDE - in addition to the primary download value). In this case, since we have
 *  a complex data type, the data will be serialized as far as redux-form is concerned.
 *  The object serialized is shaped like this:
 *
 *  {
 *    mode: ('ALL', 'INCLUDE', 'EXCLUDE'),
 *    value: <array> in the case of multiple, <string, number or bool> in the case of single select.
 *  }
 */
class DropdownInputField extends Component {
  static propTypes = {
    fieldProps: PropTypes.object.isRequired,
    showError: PropTypes.bool,
    compact: PropTypes.bool,
    forceRefreshOnLoad: PropTypes.bool,
  }

  static defaultProps = {
    compact: false,
  }

  constructor(props) {
    super(props)
    const { fieldProps } = props
    if (!fieldProps.multiple && fieldProps.modeSelect)
      console.warn(
        'WARNING: using modeSelect without multiple is not fully tested.'
      )

    this.state = {
      modeValue: '',
      dropdownValue: '',
      multiOptions: fieldProps.options || [],
      searchQuery: '',
      forcedRefresh: fieldProps.forceRefreshOnLoad,
      focused: false,
      ...this.deserializeValue(_.get(fieldProps, 'value')),
    }
  }

  /**
   * If modeSelect, will convert object to string so that redux-form can deal with it.
   */
  serializeValue = value => {
    const { modeSelect } = this.props.fieldProps
    return modeSelect
      ? JSON.stringify({
          mode: this.state.modeValue,
          value,
        })
      : value
  }

  /**
   * Deals with bogus initial values.  Semantic multi Dropdown needs an array, doesn't like undefined or string.
   */
  cleanMultiInputData = value => {
    if (!value || (_.isArray(value) && typeof value[0] === 'undefined')) {
      value = []
    } else if (typeof value === 'string') {
      value = [value]
    }

    return value
  }

  /**
   * Deserializes value if modeSelect is enabled.  Otherwise just uses value.
   * Returns object with modeValue and dropdownValue, irrespective of modeSelect enabled or not
   */
  deserializeValue = value => {
    const { modeSelect, multiple } = this.props.fieldProps
    let dropdownValue = value
    let modeValue = ''

    if (modeSelect) {
      let parsedValue
      try {
        parsedValue = !_.isUndefined(value) && JSON.parse(value)
      } catch (e) {
        // eat this error
      }
      dropdownValue = _.get(parsedValue, 'value', value)
      modeValue = _.get(parsedValue, 'mode', MODES.ALL)
    }
    dropdownValue = multiple
      ? this.cleanMultiInputData(dropdownValue)
      : dropdownValue
    return { modeValue, dropdownValue }
  }

  /**
   * NOTE: also called directly from onModeChange to force update on input value.
   */
  onChange = (e, data) => {
    const { onChange, multiple } = this.props.fieldProps
    const value = data ? data.value : this.state.dropdownValue
    onChange && onChange(this.serializeValue(value))
    !multiple && this.setState({ searchQuery: '' })
  }

  onBlur = (e, data) => {
    /**
     * Commented this out because it was causing a bug where clicking the option causes an immediate blur in the latest
     * version of Chrome - which in turn would basically onBlur the field before the value was set.
     *
     *   const { onBlur } = this.props.fieldProps
     *   const value = data && data.value
     *   onBlur && onBlur(this.serializeValue(value))
     *
     * TBH - not sure why we had to do this to begin with, but even if you remove this whole onBlur method altogether,
     * it still fails with the latest version of Chrome.
     *
     * NOTE: this effectively disables onBlur for dropdownInputs :O - but at least it works.
     */
    this.setState({ focused: false, searchQuery: '' })
    if (this.state.innerRef)
      _.defer(() => {
        // TODO: Need to debug this a little more. Been like this for years so I didn't feel confident changeing it
        // while upgrading linters.
        /* eslint-disable-next-line react/no-direct-mutation-state */
        this.state.innerRef.scrollTop = 0
      })
  }

  onFocus = (e, data) => {
    this.setState({ focused: true })
  }

  onModeChange = (e, data) => {
    const { multiple } = this.props.fieldProps
    if (data.value === MODES.ALL) {
      this.setState(
        {
          modeValue: data.value,
          dropdownValue: multiple ? [] : '',
        },
        this.onChange
      )
    } else {
      this.setState({ modeValue: data.value }, this.onChange)
    }
  }

  /**
   * Here we intercept the onSearchChange coming from semantic just so that we can
   * keep track of the searchQuery and use it later when forcing a re-query.
   */
  onSearchChange = (e, data) => {
    this.setState({ searchQuery: data.searchQuery })
    this.props.fieldProps.onSearchChange &&
      this.props.fieldProps.onSearchChange(e, data)
  }

  /**
   * Given a list of options, add new options created from values to ensure our list has currently selected options
   * @param {array} currentOptions
   * @param {array} values
   */
  mergeValuesOntoOptions(options, values) {
    const newOptions = [...options]
    _.each(values, val => {
      if (!_.find(newOptions, { value: val })) {
        newOptions.unshift({ value: val, text: val })
      }
    })
    return newOptions
  }

  /**
   * Async suggestions are tricky because we have to ensure that the selected values are included,
   * However, when we remove a selected value, we need to potentially have that value show back
   * up in the options list (depending if the removed value meets the search query criteria).
   *
   * This is also tricky, as we can't use the selected value data (as it's just the value) to stick
   * into the list, as the list will most likely be showing additional fields.  Therefore,
   * when we remove, we force a re-query the list by calling onSearchChange with the current query.
   */
  handleAsyncSuggestions = newValue => {
    const { dropdownValue: oldValue } = this.state
    const { options: curOptions } = this.props.fieldProps
    if (!_.isEmpty(_.difference(oldValue, newValue))) {
      this.setState({ multiOptions: [], forcedRefresh: true })
    } else {
      const newOptions = this.mergeValuesOntoOptions(curOptions, newValue) // merge values in
      this.setState({ multiOptions: newOptions })
    }
  }

  componentDidUpdate = prevProps => {
    const { forcedRefresh } = this.state
    const { value: prevValue, options: prevOptions } = prevProps.fieldProps
    const {
      value: curValue,
      options: curOptions,
      multiple,
      asyncSuggestions,
    } = this.props.fieldProps
    const curParsed = this.deserializeValue(curValue)

    const valuesChanged = curValue !== prevValue
    const optionsChanged = !_.isEqual(curOptions, prevOptions)

    if (valuesChanged || optionsChanged || forcedRefresh) {
      if (multiple && asyncSuggestions) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({ forcedRefresh: false }, () => {
          this.handleAsyncSuggestions(curParsed.dropdownValue)
        })
      }

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(curParsed) // sets modeValue and dropdownValue
    }
  }

  render() {
    const {
      modeValue,
      dropdownValue,
      multiOptions,
      searchQuery,
      focused,
      innerRef,
    } = this.state
    const { fieldProps, showError } = this.props
    const {
      elmId,
      placeholder,
      modeSelect,
      disabled,
      multiple,
      options,
      asyncSuggestions,
      onSearchChange, // eslint-disable-line
      forceRefreshOnLoad, // eslint-disable-line
      compact,
      className,
      customModeOptions,
      ...rest
    } = fieldProps // eslint-disable-line

    // overwrite the input props that came from semantic
    const newInput = {
      onBlur: this.onBlur,
      onChange: this.onChange,
      value: dropdownValue,
      onFocus: this.onFocus,
    }

    const newPlaceholder =
      modeValue === MODES.ALL ? 'All selected...' : placeholder || 'Select'
    const newOptions = (multiple ? multiOptions : options) || []

    /**
     * This is the dropdown's search filtering.  When async, we already have a filtered list, so we want
     * to effectively disable the normal string searching it does when the user types.  To do this we
     * change the filtering function to identity, which will return each filter value back - so essentially
     * keeping all values in the (fetched) option list.
     */
    const searchFilter = asyncSuggestions ? _.identity : true
    const newClassName = classnames({
      compact,
      [className]: true,
    })

    return (
      <div className={cx('dropdown', { 'with-mode-select': modeSelect })}>
        {modeSelect && (
          <Select
            data-elm-id={`${elmId}_mode_select`}
            disabled={!!disabled}
            options={customModeOptions || MODE_OPTIONS}
            onChange={this.onModeChange}
            className={`${cx('mode-select')} ${newClassName}`}
            value={modeValue}
            label={`${elmId}_mode_select_label`}
          />
        )}
        {/* <Ref innerRef={node => this.setState({ innerRef: node })}> */}
        <Ref
          innerRef={node => {
            if (!innerRef && node) {
              this.setState({ innerRef: node })
            }
          }}
        >
          <Dropdown
            search={searchFilter}
            onSearchChange={this.onSearchChange}
            searchQuery={searchQuery}
            multiple={multiple}
            options={newOptions}
            selectOnBlur={false}
            selection
            placeholder={newPlaceholder}
            error={showError}
            data-elm-id={`${elmId}_select`}
            label={`${elmId}_select_label`}
            className={`${cx('dropdown-field', { focused })} ${
              compact ? 'compact' : ''
            }`}
            disabled={!!disabled || modeValue === MODES.ALL}
            {...rest}
            {...newInput}
          />
        </Ref>
      </div>
    )
  }
}

const DropdownInputFieldWrapped = formFieldWrapper((fieldProps, showError) => {
  return <DropdownInputField fieldProps={fieldProps} showError={showError} />
})

DropdownInputFieldWrapped.propTypes = {
  options: PropTypes.array.isRequired,
  modeSelect: PropTypes.bool,
  customModeOptions: PropTypes.array,
  asyncSuggestions: PropTypes.bool,
  ...commonPropTypes,
}

/**
 * Utils for user of this field to serialize/deserialize data
 * Assumes multiple select mode is in use
 */
DropdownInputFieldWrapped.serializeValue = obj => JSON.stringify(obj || {})

DropdownInputFieldWrapped.deserializeValue = s => {
  let parsed = { mode: '', value: [] }
  try {
    parsed = JSON.parse(s)
  } catch (e) {
    // eat this error
  }
  return parsed
}

DropdownInputFieldWrapped.MODES = MODES
DropdownInputFieldWrapped.MODE_OPTIONS = MODE_OPTIONS

export default DropdownInputFieldWrapped
